import { apiClient } from '@borradh-workspace/api-client';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeys } from '@/lib/query-keys';

/**
 * Memories Claire parked beside the chat (web only).
 * The owner answers them straight against the API — never through a chat
 * message — so the conversation does not stop for them.
 */

export const openMemoryProposalsQueryOptions = (tokens: readonly string[]) =>
  queryOptions({
    queryKey: queryKeys.assistant.openMemoryProposals(tokens),
    queryFn: () =>
      apiClient.post<{ open: string[] }>('assistant/memory-proposals/open', {
        tokens,
      }),
    enabled: tokens.length > 0,
    staleTime: 60 * 1000,
  });

/** Which of these proposals can still be answered (answered ones stay gone after a reload). */
export const useOpenMemoryProposals = (tokens: readonly string[]) => {
  const query = useQuery(openMemoryProposalsQueryOptions(tokens));
  return {
    open: query.data?.open ?? null,
    isLoading: query.isLoading,
  };
};

export interface AnswerMemoryProposalInput {
  token: string;
  decision: 'save' | 'dismiss';
  /** Save only: also remove the similar memory it would replace. */
  replace?: boolean;
}

export const useAnswerMemoryProposal = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ token, decision, replace }: AnswerMemoryProposalInput) =>
      apiClient.post<{ status: 'saved' | 'dismissed' }>(
        `assistant/memory-proposals/${encodeURIComponent(token)}/${decision}`,
        decision === 'save' ? { replace: Boolean(replace) } : {}
      ),
    onSettled: (_data, _error, { decision }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.assistant.memoryProposals(),
      });
      if (decision === 'save') {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.assistant.memories(),
        });
      }
    },
  });
  return {
    answerMemoryProposalAsync: mutation.mutateAsync,
    isAnswering: mutation.isPending,
  };
};
